import _ from "lodash";
import { type UnknownAction } from "@reduxjs/toolkit";

import {
  ExploreCourseSummary,
  FetchPublicCourseErrorResponse,
  FetchPublicCourseRequest,
  FetchPublicCourseResponse,
  FetchPublicCoursesErrorResponse,
  FetchPublicCoursesRequest,
  FetchPublicCoursesResponse,
  UpdateSkillPreference,
} from "src/types/coaching/courses";

import {
  FETCH_PUBLIC_COURSE_REQUEST,
  FETCH_PUBLIC_COURSES_REQUEST,
  FETCHING_PREFERENCES_SKILLS_SUCCEEDED,
  FETCHING_PUBLIC_COURSE_FAILED,
  FETCHING_PUBLIC_COURSE_SUCCEEDED,
  FETCHING_PUBLIC_COURSES_FAILED,
  FETCHING_PUBLIC_COURSES_SUCCEEDED,
  UPDATE_SKILLS_PREFERENCES_REQUEST,
  UPDATING_SKILLS_PREFERENCES_SUCCEEDED,
} from "src/redux/actions/actionTypes";
import { UpdateSkillsRequest, UpdateSkillsResponse } from "src/types/coaching/skills";

const course_type_lookup = {
  COURSE: "courses",
  SIMULATION: "simulations",
  ALL: "all",
};

interface ExploreState {
  public: {
    all: ExploreCourseItemsState;
    courses: ExploreCourseItemsState;
    simulations: ExploreCourseItemsState;
  };
  preferences: {
    skills: UpdateSkillPreference[];
    selectedSkills: UpdateSkillPreference[];
  };
}

interface ExploreCourseItemsState {
  loading: boolean;
  items: ExploreCourseSummary[];
  error: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

const inititalExploreCourseItemsState: ExploreCourseItemsState = {
  loading: false,
  items: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialState: ExploreState = {
  public: {
    all: inititalExploreCourseItemsState,
    courses: inititalExploreCourseItemsState,
    simulations: inititalExploreCourseItemsState,
  },
  preferences: {
    skills: [],
    selectedSkills: [],
  },
};

export function exploreReducer(
  state: ExploreState = initialState,
  action: UnknownAction,
): ExploreState {
  switch (action.type) {
    case FETCH_PUBLIC_COURSES_REQUEST: {
      const { page, courseType } = action.payload as FetchPublicCoursesRequest;

      const key = course_type_lookup[courseType];

      const exploreState = state.public[key];
      if (!exploreState) {
        return state;
      }

      return {
        ...state,
        public: {
          ...state.public,
          [key]: {
            ...exploreState,
            loading: true,
            error: null,
          },
        },
      };
    }

    case FETCHING_PUBLIC_COURSES_SUCCEEDED: {
      const { courseType, response } =
        action.payload as FetchPublicCoursesResponse;

      const key = course_type_lookup[courseType];

      const exploreState = state.public[key];
      
      if (!exploreState) {
        return state;
      }

      const existingItems: ExploreCourseSummary[] = exploreState.items;
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");
      return {
        ...state,
        public: {
          ...state.public,
          [key]: {
            ...exploreState,
            loading: false,
            items: records,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1,
            },
          },
        },
      };
    }

    case FETCHING_PUBLIC_COURSES_FAILED: {
      const { courseType, error } =
        action.payload as FetchPublicCoursesErrorResponse;

      const key = course_type_lookup[courseType];

      const exploreState = state.public[key];
      if (!exploreState) {
        return state;
      }

      return {
        ...state,
        public: {
          ...state.public,
          [key]: {
            ...exploreState,
            loading: false,
            items: [],
            error: error,
          },
        },
      };
    }

    case UPDATE_SKILLS_PREFERENCES_REQUEST: {
      const { skills } = action.payload as UpdateSkillsRequest;
      const selectedSkills = state.preferences.selectedSkills || [];
      const uniqueSelectedSkills = _.uniqBy([...selectedSkills, ...skills], "id");
      
      return {
        ...state,
        preferences: {
          ...state.preferences,
          skills: uniqueSelectedSkills
        },
      };
    }

    case UPDATING_SKILLS_PREFERENCES_SUCCEEDED: {
      const { skills } = action.payload as UpdateSkillsResponse;
      
      const selectedSkills = skills.filter((skill: UpdateSkillPreference) => {
        return skill.selected;
      });
     

      return {
        ...state,
        preferences: {
          ...state.preferences,
          selectedSkills: selectedSkills,
        },
      };
    }

    case FETCHING_PREFERENCES_SKILLS_SUCCEEDED: {
      const { response } = action.payload as any;
      const skills = response.skills || [];

      const selectedSkills = skills.map(skillId => {
        return {
          id: skillId,
          selected: true,
        }
      }); 

      return {
        ...state,
        preferences: {
          ...state.preferences,
          selectedSkills: selectedSkills,
        },
      };
    }

    default:
      return state;
  }
}
